import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const NAVY = '#1e3a5f';

/**
 * Reusable pagination control.
 * Props:
 *   page      - current page (1-indexed)
 *   pages     - total number of pages
 *   total     - total number of items
 *   limit     - items per page
 *   onChange  - (newPage: number) => void
 */
export default function Pagination({ page, pages, total, limit, onChange }) {
  if (!pages || pages <= 1) return null;

  const from = (page - 1) * limit + 1;
  const to   = Math.min(page * limit, total);

  const getPageNumbers = () => {
    if (pages <= 7) return Array.from({ length: pages }, (_, i) => i + 1);
    if (page <= 4)          return [1, 2, 3, 4, 5, '…', pages];
    if (page >= pages - 3)  return [1, '…', pages-4, pages-3, pages-2, pages-1, pages];
    return [1, '…', page-1, page, page+1, '…', pages];
  };

  const btnBase = {
    display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
    height: 32, minWidth: 32, borderRadius: 6, fontSize: 13, fontWeight: 500,
    border: '1px solid #e2e8f0', cursor: 'pointer', padding: '0 8px',
    transition: 'background 0.15s, color 0.15s, border-color 0.15s',
    userSelect: 'none',
  };

  return (
    <div className="flex items-center justify-between gap-4 flex-wrap pt-2">
      {/* Showing X–Y of Z */}
      <p className="text-xs" style={{ color: '#94a3b8' }}>
        Showing <strong style={{ color: '#64748b' }}>{from}–{to}</strong> of{' '}
        <strong style={{ color: '#64748b' }}>{total}</strong>
      </p>

      {/* Controls */}
      <div className="flex items-center gap-1">
        {/* Prev */}
        <button
          onClick={() => onChange(page - 1)}
          disabled={page === 1}
          style={{
            ...btnBase,
            background: page === 1 ? '#f8fafc' : 'white',
            color: page === 1 ? '#cbd5e1' : '#475569',
            cursor: page === 1 ? 'not-allowed' : 'pointer',
          }}>
          <ChevronLeft size={15} />
        </button>

        {/* Page numbers */}
        {getPageNumbers().map((p, i) =>
          p === '…' ? (
            <span key={`ellipsis-${i}`} style={{ ...btnBase, border: 'none', background: 'transparent', color: '#94a3b8', cursor: 'default' }}>
              …
            </span>
          ) : (
            <button
              key={p}
              onClick={() => onChange(p)}
              style={{
                ...btnBase,
                background: p === page ? NAVY : 'white',
                color: p === page ? 'white' : '#475569',
                borderColor: p === page ? NAVY : '#e2e8f0',
                fontWeight: p === page ? 700 : 500,
              }}>
              {p}
            </button>
          )
        )}

        {/* Next */}
        <button
          onClick={() => onChange(page + 1)}
          disabled={page === pages}
          style={{
            ...btnBase,
            background: page === pages ? '#f8fafc' : 'white',
            color: page === pages ? '#cbd5e1' : '#475569',
            cursor: page === pages ? 'not-allowed' : 'pointer',
          }}>
          <ChevronRight size={15} />
        </button>
      </div>
    </div>
  );
}
